import { type FeatureFlagOverrides } from '@canvas/client-feature-flags-state';
import {
  QUERY_PARAM_DARK_FEATURES,
  QUERY_PARAM_FEATURE_FLAGS,
} from '@canvas/constants/query-params';

import { getOverrides } from './feature-flag-persisted-overrides';

const DISABLE_PREFIX = 'disable__';

function removeDisablePrefix(flag: string): string {
  return flag.replace(DISABLE_PREFIX, '');
}

export function getOverridesFromLocalDev(): Record<string, boolean> {
  try {
    const devOverrides: Record<string, boolean> = process.env.FEATURE_FLAG_DEV_OVERRIDES
      ? JSON.parse(process.env.FEATURE_FLAG_DEV_OVERRIDES)
      : {};
    return devOverrides;
  } catch (e) {
    console.info(
      `Cannot parse malformed FEATURE_FLAG_DEV_OVERRIDES: ${process.env.FEATURE_FLAG_DEV_OVERRIDES}`,
    );
    return {};
  }
}

export const getFeatureFlagOverrides = (urlString?: string): FeatureFlagOverrides => {
  const hashOverrideParams: Record<string, boolean> = {};
  const overrideParams: Record<string, boolean> = {};
  if (urlString) {
    const url = new URL(urlString);
    /**
     * This is exists because most devs tend to add `?df=<...>` to the _end_ of the URL without thinking about how the URL will actually be parsed.
     *
     * The use case where adding `?` to the end of the URL won't work is if you have a `#` somewhere in the URL. Anything after the # is
     * considered part of the "hash" segment of the URL, even `?`/query-param looking string segments.
     *
     * As such, we handle `?` in the `#` part of the URL specially for this use case. This is use case is particularly useful for cases like
     * hash routing, where `#` is _always_ a very large part of the URL and it's hard to quickly tell where real query params need to be inserted
     */
    const hashFragments = url.hash.split('?');
    const hashParamsString = hashFragments[hashFragments.length - 1] ?? '';

    new URLSearchParams(hashParamsString)
      .getAll(QUERY_PARAM_DARK_FEATURES)
      .forEach((flag) => {
        if (flag) {
          hashOverrideParams[removeDisablePrefix(flag)] = flag.startsWith(DISABLE_PREFIX)
            ? false
            : true;
        }
      });

    url.searchParams?.getAll(QUERY_PARAM_DARK_FEATURES)?.forEach((flag) => {
      if (flag) {
        overrideParams[removeDisablePrefix(flag)] = flag.startsWith(DISABLE_PREFIX)
          ? false
          : true;
      }
    });

    // handle new QUERY_PARAM_FEATURE_FLAGS
    new URLSearchParams(hashParamsString)
      .getAll(QUERY_PARAM_FEATURE_FLAGS)
      .forEach((param) => {
        const [flag, value] = param.split(':');
        if (flag) {
          hashOverrideParams[removeDisablePrefix(flag)] = flag.startsWith(DISABLE_PREFIX)
            ? false
            : value?.toLowerCase() === 'false'
              ? false
              : true;
        }
      });

    url.searchParams?.getAll(QUERY_PARAM_FEATURE_FLAGS)?.forEach((param) => {
      const [flag, value] = param.split(':');
      if (flag) {
        overrideParams[removeDisablePrefix(flag)] =
          value?.toLowerCase() === 'false' || flag.startsWith(DISABLE_PREFIX)
            ? false
            : true;
      }
    });
  }

  // We also want to check process.env.FEATURE_FLAG_DEV_OVERRIDES for any overrides we set in
  // .ff-localdev
  const devOverrides = getOverridesFromLocalDev();

  // Get persisted overrides from local storage
  const persistedOverrides = getOverrides();

  return Object.fromEntries([
    ...Object.entries(hashOverrideParams),
    ...Object.entries(overrideParams),
    ...Object.entries(devOverrides),
    ...Object.entries(persistedOverrides),
  ]);
};
